import { useState, type CSSProperties } from 'react';
import { formatCurrency, formatNumber } from './format';
import { pointsToDollarsAt, type PricingRecalcOutput } from './hooks/usePricingRecalc';
import type { RevenueBucket } from '../utils/netRevenueMath';
import type { ProspectLoan } from '../types';

interface PriceWaterfallLedgerProps {
  loan: ProspectLoan;
  recalc: PricingRecalcOutput;
}

/**
 * Bucket → vertical bar color inside the BEM breakdown table. Matches the
 * inline `style="--c: var(...)"` on the handoff's three rows
 * (`Product Pricing.html:3856/3871/3886`).
 */
const BUCKET_COLOR: Record<RevenueBucket, string> = {
  margin: 'var(--accent)',
  level1: 'var(--borrower)',
  branch_management: 'var(--accent-2)',
  other: 'var(--investor)',
  lo_compensation: 'var(--ink-3)'
};

/** Pretty-name "Uncategorized" → "Lendwise Margin" without hitting the
 *  org-override helper (Gate 2 will do the full org-aware lookup). */
function displayLabel(raw: string): string {
  const n = raw.trim().toLowerCase();
  if (n === 'uncategorized') {
    return 'Lendwise Margin';
  }
  return raw;
}

/** "MM/DD/YYYY at h:mm AM/PM" matching the handoff's bem-meta format. */
function formatLastUpdated(iso: string): string {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${date} at ${time}`;
  } catch {
    return iso;
  }
}

/**
 * Dual-path ledger per the handoff. Borrower/par path followed by investor
 * path, with the terminal LO Profitability row. Styling comes from
 * `styles.css` `.ledger` / `.row` / `.path-label` rules. Respects the
 * dual-path rule (memory `feedback_pricing_model_dual_path.md`): the two
 * paths branch from Total price locked, never sequential.
 *
 * Back-end margin row is expandable. The collapsed headline renders the
 * current stack after concessions / LO comp reduction so pending edits are
 * visible without opening the detail table. When that current stack differs
 * from Encompass's fixed original stack, a release row carries the delta so
 * the investor path still reconciles to the fixed Investor Lock Price.
 */
export function PriceWaterfallLedger({ loan, recalc }: PriceWaterfallLedgerProps) {
  const {
    loanAmount,
    basePriceLocked,
    discountPoints,
    concessionPoints,
    totalLoLocked,
    locrPoints,
    adjustedLoLocked,
    availableLcPts,
    lenderCreditPoints,
    curePoints,
    netLoLocked,
    investorLockPrice,
    companyProfitDollars,
    bonaFide,
    belowPar,
    bemRows,
    bemOriginalTotal,
    bemCurrentTotal,
    bemHasUpdates
  } = recalc;

  const [bemOpen, setBemOpen] = useState(false);

  const $ = (pts: number) => pointsToDollarsAt(loanAmount, pts);

  const bonaClass = bonaFide === 'Y' ? 'bona-chip bona-y' : bonaFide === 'N' ? 'bona-chip bona-n' : 'bona-chip bona-blank';
  const bonaLabel = bonaFide === 'Y' ? 'bona-fide Y' : bonaFide === 'N' ? 'bona-fide N' : 'bona-fide —';
  const profitClass = companyProfitDollars >= 0 ? 'row profit-pos' : 'row profit-neg';

  const totalDelta = bemCurrentTotal - bemOriginalTotal;
  const totalDeltaClass = Math.abs(totalDelta) < 0.0005 ? 'neu' : totalDelta > 0 ? 'pos' : 'neg';
  const displayBemTotal = bemHasUpdates ? bemCurrentTotal : bemOriginalTotal;
  const releasedMarginPoints = Math.max(0, bemOriginalTotal - bemCurrentTotal);
  const companyProfitPoints = loanAmount > 0 ? companyProfitDollars / (loanAmount * 0.01) : 0;
  const locrBps = locrPoints * 100;
  const locrBpsDecimals = Math.abs(locrBps - Math.round(locrBps)) > 0.0001 ? 2 : 0;

  return (
    <div className="ledger" aria-label="Price waterfall">
      {/* Borrower / Par path */}
      <div className="path-label borrower">
        <span className="bar" />
        <span>Borrower / Par path</span>
        <span className="path-hint">how the borrower's rate produces the LO lock price</span>
      </div>

      <div className="row">
        <div className="name">
          Discount points <span className="raw">({formatNumber(discountPoints, 3)}%)</span>
          <span className={bonaClass}>{bonaLabel}</span>
        </div>
        <div className="num tab pos">+{formatNumber(discountPoints, 3)}</div>
        <div className="num tab pos">+{formatCurrency($(discountPoints))}</div>
      </div>

      {concessionPoints !== 0 && (
        <div className="row" style={{ background: 'linear-gradient(90deg, var(--pos-soft) 0%, transparent 40%)' }}>
          <div className="name" style={{ color: 'var(--pos-ink)' }}>
            + Concessions
            <span className="raw" style={{ color: 'var(--pos-ink)' }}>(LO absorbs)</span>
          </div>
          <div className="num tab pos">+{formatNumber(concessionPoints, 3)}</div>
          <div className="num tab pos">+{formatCurrency($(concessionPoints))}</div>
        </div>
      )}

      <div className="row sub">
        <div className="name">Total LO locked price</div>
        <div className="num tab">{formatNumber(totalLoLocked, 3)}</div>
        <div className="num tab" />
      </div>

      {locrPoints > 0 && (
        <div className="row indent">
          <div className="name">+ LO comp reduction <span className="raw">{formatNumber(locrBps, locrBpsDecimals)} bps released</span></div>
          <div className="num tab pos">+{formatNumber(locrPoints, 3)}</div>
          <div className="num tab pos">+{formatCurrency($(locrPoints))}</div>
        </div>
      )}

      <div className="row sub">
        <div className="name">Adjusted total LO locked</div>
        <div className="num tab">{formatNumber(adjustedLoLocked, 3)}</div>
        <div className="num tab" />
      </div>

      <div className="row indent">
        <div className="name" style={{ color: 'var(--pos-ink)' }}>Available lender credit above par</div>
        <div className="num tab pos">+{formatNumber(availableLcPts, 3)}</div>
        <div className="num tab pos">{formatCurrency($(availableLcPts))}</div>
      </div>

      <div className="row indent">
        <div className="name">− Lender credit used</div>
        <div className="num tab neg">−{formatNumber(lenderCreditPoints, 3)}</div>
        <div className="num tab neg">
          {lenderCreditPoints > 0 ? `−${formatCurrency($(lenderCreditPoints))}` : ''}
        </div>
      </div>

      {curePoints > 0 && (
        <div className="row indent">
          <div className="name">
            − Cost to cure
            <span className="lock-chip" title="Cost to cure cannot be reduced once set.">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              locked
            </span>
          </div>
          <div className="num tab neg">−{formatNumber(curePoints, 3)}</div>
          <div className="num tab neg">−{formatCurrency($(curePoints))}</div>
        </div>
      )}

      <div className={`row sub${belowPar ? ' error' : ''}`} style={{ background: 'var(--borrower-soft)' }}>
        <div className="name">Net LO locked price</div>
        <div className="num tab">{formatNumber(netLoLocked, 3)}</div>
        <div className="num tab" />
      </div>

      {/* Investor path */}
      <div className="path-label investor">
        <span className="bar" />
        <span>Investor path</span>
        <span className="path-hint">margin stack that builds the price we deliver the investor</span>
      </div>

      <div className="row sub">
        <div className="name">Total price locked</div>
        <div className="num tab">{formatNumber(basePriceLocked, 3)}</div>
        <div className="num tab" />
      </div>

      <div className={`row indent expandable${bemOpen ? ' expanded' : ''}`}>
        <div className="name">
          <button
            type="button"
            className="expand-btn"
            onClick={() => setBemOpen((v) => !v)}
            aria-label={bemOpen ? 'Collapse back-end margin breakdown' : 'Expand back-end margin breakdown'}
            aria-expanded={bemOpen}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
          <span>+ Back-end margin</span>
          {bemHasUpdates && <span className="upd-chip">updated</span>}
        </div>
        <div className="num tab pos">+{formatNumber(displayBemTotal, 3)}</div>
        <div className="num tab pos">+{formatCurrency($(displayBemTotal))}</div>
      </div>

      {bemOpen && (
        <div className="row-detail open">
          <div className="bem-table">
            <div className="bem-head">
              <div>Component</div>
              <div className="num">Original</div>
              <div className="num">Current</div>
              <div className="num">Δ</div>
            </div>
            {bemRows.map((row, idx) => {
              const rowDelta = row.updatedPoints - row.originalPoints;
              const rowClass = Math.abs(rowDelta) < 0.0005 ? 'neu' : rowDelta > 0 ? 'pos' : 'neg';
              const barColor = BUCKET_COLOR[row.bucket] ?? 'var(--ink-3)';
              return (
                <div className="bem-row" key={`${row.bucket}-${idx}`}>
                  <div className="bem-name">
                    <span className="bem-bar" style={{ '--c': barColor } as CSSProperties} />
                    {displayLabel(row.description)}
                  </div>
                  <div className="num tab">
                    <div className="mono">{formatNumber(row.originalPoints, 3)}</div>
                    <div className="bem-sub mono">{formatCurrency($(row.originalPoints))}</div>
                  </div>
                  <div className="num tab">
                    <div className="mono">{formatNumber(row.updatedPoints, 3)}</div>
                    <div className="bem-sub mono">{formatCurrency($(row.updatedPoints))}</div>
                  </div>
                  <div className="num">
                    <span className={`delta-chip ${rowClass}`}>
                      {rowClass === 'neu' ? '—' : `${rowDelta > 0 ? '+' : ''}${formatNumber(rowDelta, 3)}`}
                    </span>
                  </div>
                </div>
              );
            })}
            <div className="bem-foot">
              <div>Total back-end margin</div>
              <div className="num mono tab">{formatNumber(bemOriginalTotal, 3)}</div>
              <div className="num mono tab">{formatNumber(bemCurrentTotal, 3)}</div>
              <div className="num">
                <span className={`delta-chip ${totalDeltaClass}`}>
                  {totalDeltaClass === 'neu' ? '—' : `${totalDelta > 0 ? '+' : ''}${formatNumber(totalDelta, 3)}`}
                </span>
              </div>
            </div>
          </div>
          {loan.rate_lock_pricing_updated && (
            <div className="bem-meta">
              Last updated <b>{formatLastUpdated(loan.rate_lock_pricing_updated)}</b>
              <span className="dot-sep">·</span>
              <a href="#pv2-history">view in history</a>
            </div>
          )}
        </div>
      )}

      {releasedMarginPoints > 0 && (
        <div className="row indent">
          <div className="name">+ Margin released to borrower path</div>
          <div className="num tab pos">+{formatNumber(releasedMarginPoints, 3)}</div>
          <div className="num tab pos">+{formatCurrency($(releasedMarginPoints))}</div>
        </div>
      )}

      {/*
       * Investor lock price is fixed by Encompass. In the unedited case:
       * basePriceLocked + bemOriginalTotal = investorLockPrice. When local
       * waterfall edits reduce the current margin, the release row above
       * carries the difference so the displayed investor path still adds up:
       * basePriceLocked + bemCurrentTotal + releasedMargin = investorLockPrice.
       * Dollars column shows the above-par delta, not absolute × loan.
       */}
      {(() => {
        const ledgerInvestorAbovePar = investorLockPrice - 100;
        const ledgerInvestorUsd = $(ledgerInvestorAbovePar);
        return (
          <div className="row sub" style={{ background: 'var(--investor-soft)' }}>
            <div className="name">Investor lock price</div>
            <div className="num tab">{formatNumber(investorLockPrice, 3)}</div>
            <div className="num tab">
              {ledgerInvestorUsd >= 0 ? formatCurrency(ledgerInvestorUsd) : `−${formatCurrency(Math.abs(ledgerInvestorUsd))}`}
            </div>
          </div>
        );
      })()}

      {/* Terminal */}
      <div className={profitClass}>
        <div className="name">LO Profitability</div>
        <div className="num tab">
          {companyProfitDollars >= 0 ? '+' : ''}
          {formatNumber(companyProfitPoints, 3)}
        </div>
        <div className="num tab">
          {companyProfitDollars >= 0 ? '+' : ''}
          {formatCurrency(companyProfitDollars)}
        </div>
      </div>
    </div>
  );
}
